import type { DetailField } from './loanDetails'
import type { Lead } from './mockData'

type ContactExtras = {
  fico?: string
  cashOutValue?: string
  currentLoanType?: string
  currentMortgageBalance?: string
  currentInterestRate?: string
  currentLenderName?: string
  contactStatus?: string
  currentLtv?: string
  previousLoanFundedDate?: string
  contactOwner?: string
  accountName?: string
  bestContactNumber?: string
  mobile?: string
  leadSource?: string
  totalInstallmentDebt?: string
  totalInstallmentDebtPayment?: string
  totalCreditCardDebt?: string
  totalCreditCardDebtPayment?: string
}

/** Credit and current-mortgage detail synced from the contact record. */
export const contactExtras: Record<string, ContactExtras> = {
  l1: {
    fico: '712',
    currentLoanType: 'Conventional',
    currentMortgageBalance: '$248,900.00',
    currentInterestRate: '6.875%',
    currentLenderName: 'Rocket Mortgage',
    contactStatus: 'Working',
    currentLtv: '68.400%',
    contactOwner: 'Calvin Dado',
    accountName: 'Calvin Dado Database',
    bestContactNumber: 'Mobile',
    leadSource: 'Zillow',
    totalInstallmentDebt: '$41,220.00',
    totalInstallmentDebtPayment: '$720.00',
    totalCreditCardDebt: '$6,140.00',
    totalCreditCardDebtPayment: '$184.00',
  },
  l2: {
    fico: '689',
    cashOutValue: '$45,000.00',
    currentLoanType: 'FHA',
    currentMortgageBalance: '$188,430.00',
    currentInterestRate: '5.625%',
    currentLenderName: 'Freedom Mortgage',
    contactStatus: 'Nurture',
    currentLtv: '74.100%',
    previousLoanFundedDate: 'Mar 14, 2021',
    contactOwner: 'Calvin Dado',
    accountName: 'Calvin Dado Database',
    leadSource: 'Fincast',
    totalInstallmentDebt: '$63,910.00',
    totalInstallmentDebtPayment: '$1,104.00',
    totalCreditCardDebt: '$9,880.00',
    totalCreditCardDebtPayment: '$310.00',
  },
  l3: {
    fico: '744',
    currentLoanType: 'Conventional',
    contactStatus: 'New',
    contactOwner: 'Calvin Dado',
    accountName: 'Calvin Dado Database',
    leadSource: 'Referral',
    totalInstallmentDebt: '$22,500.00',
    totalInstallmentDebtPayment: '$465.00',
    totalCreditCardDebt: '$3,210.00',
    totalCreditCardDebtPayment: '$96.00',
  },
  l4: {
    fico: '766',
    currentLoanType: 'Conventional',
    contactStatus: 'Loan In Process',
    contactOwner: 'Calvin Dado',
    accountName: 'Calvin Dado Database',
    bestContactNumber: 'Mobile',
    leadSource: 'Zillow',
    totalInstallmentDebt: '$54,780.00',
    totalInstallmentDebtPayment: '$988.00',
    totalCreditCardDebt: '$7,455.00',
    totalCreditCardDebtPayment: '$225.00',
  },
  l5: {
    fico: '698',
    currentLoanType: 'VA',
    currentMortgageBalance: '$96,346.00',
    currentInterestRate: '5.125%',
    contactStatus: 'Working',
    currentLtv: '51.500%',
    contactOwner: 'Calvin Dado',
    accountName: 'Calvin Dado Database',
    leadSource: 'VA Cashout Prospect',
    totalInstallmentDebt: '$98,411.00',
    totalInstallmentDebtPayment: '$1,669.00',
    totalCreditCardDebt: '$4,376.00',
    totalCreditCardDebtPayment: '$136.00',
  },
  l6: {
    fico: '702',
    cashOutValue: '$60,000.00',
    currentLoanType: 'Conventional',
    currentMortgageBalance: '$262,150.00',
    currentInterestRate: '7.125%',
    currentLenderName: 'Truist',
    contactStatus: 'Loan In Process',
    currentLtv: '79.721%',
    previousLoanFundedDate: 'Jun 2, 2022',
    contactOwner: 'Calvin Dado',
    accountName: 'Calvin Dado Database',
    leadSource: 'Referral',
    totalInstallmentDebt: '$37,600.00',
    totalInstallmentDebtPayment: '$742.00',
    totalCreditCardDebt: '$12,040.00',
    totalCreditCardDebtPayment: '$388.00',
  },
  l7: {
    fico: '681',
    currentLoanType: 'FHA',
    contactStatus: 'Working',
    contactOwner: 'Calvin Dado',
    accountName: 'Calvin Dado Database',
    leadSource: 'Fincast',
    totalInstallmentDebt: '$18,900.00',
    totalInstallmentDebtPayment: '$402.00',
    totalCreditCardDebt: '$5,610.00',
    totalCreditCardDebtPayment: '$168.00',
  },
}

/** Build the Contact Information layout for a lead, mirroring the CRM record page. */
export function buildContactFields(lead: Lead): {
  left: DetailField[]
  right: DetailField[]
} {
  const extra = contactExtras[lead.id] ?? {}

  return {
    left: [
      { label: 'Name', value: lead.name.toUpperCase() },
      { label: 'FICO', value: extra.fico },
      { label: 'Cash Out Value', value: extra.cashOutValue },
      { label: 'Current Loan Type', value: extra.currentLoanType },
      { label: 'Current Mortgage Balance', value: extra.currentMortgageBalance },
      { label: 'Current Interest Rate', value: extra.currentInterestRate },
      { label: 'Current Lender Name', value: extra.currentLenderName },
      { label: 'Contact Status', value: extra.contactStatus ?? lead.stage },
      { label: 'Current LTV', value: extra.currentLtv },
      { label: 'Previous Loan Funded Date', value: extra.previousLoanFundedDate },
    ],
    right: [
      { label: 'Contact Owner', value: extra.contactOwner ?? 'Milan Rakic', person: true },
      { label: 'Account Name', value: extra.accountName },
      { label: 'Best Contact Number', value: extra.bestContactNumber },
      { label: 'Phone', value: lead.phone },
      { label: 'Mobile', value: extra.mobile },
      { label: 'Email', value: lead.email },
      { label: 'Lead Source', value: extra.leadSource ?? lead.source },
      { label: 'Total Installment Debt', value: extra.totalInstallmentDebt },
      { label: 'Total Installment Debt Payment', value: extra.totalInstallmentDebtPayment },
      { label: 'Total Credit Card Debt', value: extra.totalCreditCardDebt },
      {
        label: 'Total Credit Card Debt Payment',
        value: extra.totalCreditCardDebtPayment,
      },
    ],
  }
}
